// Content Admin Service - Especializado para operações administrativas
import type { 
  PerfilRelatorioConfig,
  ContentVersion, 
  ActiveContentInfo,
  InsumosRelatoriosContent,
  QuestionarioContent,
  MatrizAvaliacaoContent,
  MatrizGraficoAranha,
} from './contentSnapshotService';

// API Client para admin (reutiliza a implementação do contentSnapshotService)
const API_BASE_URL = '/api/v2';

class AdminApiClient {
  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const response = await fetch(url, {
      ...options,
      headers: {
        ...this.getAuthHeaders(),
        ...(options.headers as Record<string, string>),
      },
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        throw new Error(`Access denied (${response.status}). Administrative privileges required.`);
      }
      
      const error = await response.json().catch(() => ({ error: 'Network error' }));
      throw new Error(error.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

// Interfaces específicas para admin
export interface CreateVersionRequest {
  description: string;
  created_by: string;
}

export interface ValidationError {
  field: string;
  message: string;
  severity: 'error' | 'warning' | 'info';
  suggestion?: string;
}

export interface ContentSnapshot {
  questions: QuestionarioContent;
  evaluation_matrix: MatrizAvaliacaoContent;
  report_contents: InsumosRelatoriosContent;
  spider_chart_contents: MatrizGraficoAranha;
}

// Service principal para operações admin
export class ContentAdminService {
  private apiClient = new AdminApiClient();

  // === VERSION MANAGEMENT ===
  
  /**
   * Lista todas as versões de conteúdo
   */
  async listVersions(): Promise<ContentVersion[]> {
    const response = await this.apiClient.get<{ versions: ContentVersion[] }>('/admin/versions');
    return response.versions;
  }

  /**
   * Busca uma versão específica
   */
  async getVersion(versionId: string): Promise<ContentVersion> {
    const response = await this.apiClient.get<{ version: ContentVersion }>(`/admin/versions/${versionId}`);
    return response.version;
  }

  /**
   * Cria uma nova versão
   */
  async createVersion(data: CreateVersionRequest): Promise<ContentVersion> {
    const response = await this.apiClient.post<{ version: ContentVersion }>('/admin/versions', data);
    return response.version;
  }

  /**
   * Ativa uma versão específica
   */
  async activateVersion(versionId: string): Promise<void> {
    await this.apiClient.post<{ message: string }>(`/admin/versions/${versionId}/activate`);
  }

  /**
   * Deleta uma versão
   */
  async deleteVersion(versionId: string): Promise<void> {
    await this.apiClient.delete(`/admin/versions/${versionId}`);
  }

  /**
   * Rollback para uma versão anterior
   */
  async rollbackToVersion(versionId: string): Promise<void> {
    await this.apiClient.post<{ message: string }>(`/admin/versions/${versionId}/rollback`);
  }

  // === CONTENT MANAGEMENT ===

  /**
   * Busca todo o conteúdo de uma versão
   */
  async getVersionContent(versionId: string): Promise<ContentSnapshot> {
    const response = await this.apiClient.get<{ content: ContentSnapshot }>(`/admin/versions/${versionId}/content`);
    return response.content;
  }

  /**
   * Atualiza todo o conteúdo de uma versão
   */
  async updateVersionContent(versionId: string, content: ContentSnapshot): Promise<void> {
    await this.apiClient.put<{ message: string }>(`/admin/versions/${versionId}/content`, content);
  }

  /**
   * Busca questões de uma versão
   */
  async getVersionQuestions(versionId: string): Promise<QuestionarioContent> {
    const response = await this.apiClient.get<{ questions: QuestionarioContent }>(`/admin/versions/${versionId}/questions`);
    return response.questions;
  }

  /**
   * Atualiza questões de uma versão
   */
  async updateVersionQuestions(versionId: string, questions: QuestionarioContent): Promise<void> {
    await this.apiClient.put<{ message: string }>(`/admin/versions/${versionId}/questions`, questions);
  }

  /**
   * Busca matriz de avaliação de uma versão
   */
  async getVersionEvaluationMatrix(versionId: string): Promise<MatrizAvaliacaoContent> {
    const response = await this.apiClient.get<{ evaluation_matrix: MatrizAvaliacaoContent }>(`/admin/versions/${versionId}/evaluation-matrix`);
    return response.evaluation_matrix;
  }

  /**
   * Atualiza matriz de avaliação de uma versão
   */
  async updateVersionEvaluationMatrix(versionId: string, matrix: MatrizAvaliacaoContent): Promise<void> {
    await this.apiClient.put<{ message: string }>(`/admin/versions/${versionId}/evaluation-matrix`, matrix);
  }

  /**
   * Busca conteúdo de relatórios de uma versão
   */
  async getVersionReportContents(versionId: string): Promise<InsumosRelatoriosContent> {
    const response = await this.apiClient.get<{ report_contents: InsumosRelatoriosContent }>(`/admin/versions/${versionId}/report-contents`);
    return response.report_contents;
  }

  /**
   * Atualiza conteúdo de relatórios de uma versão
   */
  async updateVersionReportContents(versionId: string, contents: InsumosRelatoriosContent): Promise<void> {
    await this.apiClient.put<{ message: string }>(`/admin/versions/${versionId}/report-contents`, contents);
  }

  /**
   * Busca conteúdo de gráfico de aranha de uma versão
   */
  async getVersionSpiderChart(versionId: string): Promise<MatrizGraficoAranha> {
    const response = await this.apiClient.get<{ spider_chart: MatrizGraficoAranha }>(`/admin/versions/${versionId}/spider-chart`);
    return response.spider_chart;
  }

  /**
   * Atualiza conteúdo de gráfico de aranha de uma versão
   */
  async updateVersionSpiderChart(versionId: string, chart: MatrizGraficoAranha): Promise<void> {
    await this.apiClient.put<{ message: string }>(`/admin/versions/${versionId}/spider-chart`, chart);
  }

  // === VALIDATION & PREVIEW ===

  /**
   * Valida o conteúdo de uma versão
   */
  async validateContent(versionId: string): Promise<{ is_valid: boolean; errors: ValidationError[] }> {
    return this.apiClient.get<{ is_valid: boolean; errors: ValidationError[] }>(`/admin/versions/${versionId}/validate`);
  }

  /**
   * Preview das mudanças de uma versão
   */
  async previewVersion(versionId: string): Promise<ContentSnapshot> {
    const response = await this.apiClient.get<{ content: ContentSnapshot }>(`/admin/versions/${versionId}/preview`);
    return response.content;
  }

  // === MIGRATION & IMPORT/EXPORT ===

  /**
   * Migra conteúdo de arquivos JSON
   */
  async migrateFromJSON(sourcePath: string, description: string, createdBy: string): Promise<ContentVersion> {
    const response = await this.apiClient.post<{ version: ContentVersion; message: string }>('/admin/migrate-from-json', {
      source_path: sourcePath,
      description,
      created_by: createdBy,
    });
    return response.version;
  }

  /**
   * Exporta uma versão para JSON
   */
  async exportVersion(versionId: string): Promise<ContentSnapshot> {
    return this.apiClient.get<ContentSnapshot>(`/admin/versions/${versionId}/export`);
  }

  /**
   * Importa conteúdo para uma versão
   */
  async importToVersion(versionId: string, content: ContentSnapshot): Promise<void> {
    await this.apiClient.post<{ message: string }>(`/admin/versions/${versionId}/import`, content);
  }

  // === ACTIVE CONTENT INFO ===

  /**
   * Busca informações da versão ativa
   */
  async getActiveContentInfo(): Promise<ActiveContentInfo> {
    return this.apiClient.get<ActiveContentInfo>('/admin/active/info');
  }

  /**
   * Força refresh do conteúdo ativo
   */
  async refreshActiveContent(): Promise<void> {
    await this.apiClient.post<{ message: string }>('/admin/active/refresh');
  }

  // === UTILITY METHODS ===

  /**
   * Duplica uma versão existente
   */
  async duplicateVersion(versionId: string, newDescription: string, createdBy: string): Promise<ContentVersion> {
    // Busca conteúdo da versão original
    const content = await this.getVersionContent(versionId);
    
    // Cria nova versão
    const newVersion = await this.createVersion({
      description: newDescription,
      created_by: createdBy,
    });

    // Copia o conteúdo
    await this.updateVersionContent(newVersion.id, content);

    return newVersion;
  }

  /**
   * Verifica se o usuário tem acesso admin
   */
  async checkAdminAccess(): Promise<boolean> {
    try {
      await this.getActiveContentInfo();
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('401') || errorMessage.includes('403')) {
        return false;
      }
      throw error;
    }
  }

  /**
   * Busca estatísticas das versões
   */
  async getVersionStats(): Promise<{
    total: number;
    active: number;
    drafts: number;
    lastUpdated: string;
  }> {
    const versions = await this.listVersions();
    const active = versions.filter(v => v.is_active).length;
    const total = versions.length;
    const drafts = total - active;
    const lastUpdated = versions.length > 0 
      ? new Date(Math.max(...versions.map(v => new Date(v.created_at).getTime()))).toISOString()
      : new Date().toISOString();

    return { total, active, drafts, lastUpdated };
  }
}

// Export singleton instance
export const contentAdminService = new ContentAdminService();

// Export types
export type {
  CreateVersionRequest,
  ValidationError,
  ContentSnapshot,
};